// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/CrmDetalhe.tsx
//  CRM — Detalhe de Atendimento + Timeline de Contatos + Novo Atendimento
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Save, MessageSquare, CheckCircle2,
  Clock, User, CalendarClock, Send,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { crmService }               from '@/features/operacional/services/crmService';
import { PessoaSearchCombobox }     from '@/components/PessoaSearchCombobox';
import { EnviarWhatsAppModal }      from '@/components/EnviarWhatsAppModal';
import { getApiErrorMessage }       from '@/lib/apiClient';

import { Button }               from '@/components/ui/button';
import { Input }                from '@/components/ui/input';
import { Label }                from '@/components/ui/label';
import { Textarea }             from '@/components/ui/textarea';
import { Badge }                from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

import type {
  CriarAtendimentoRequest,
  AdicionarContatoRequest,
} from '@/features/operacional/types';

// ── Modo: novo atendimento ────────────────────────────────────────────────

function NovoAtendimentoForm() {
  const navigate    = useNavigate();
  const queryClient = useQueryClient();

  const [clienteId,          setClienteId]          = useState<number | null>(null);
  const [sAssunto,           setSAssunto]           = useState('');
  const [sDescricao,         setSDescricao]         = useState('');
  const [prioridadeId,       setPrioridadeId]       = useState('1');
  const [isResultadoEsperado, setIsResultadoEsperado] = useState(false);
  const [dtSolicitacao,      setDtSolicitacao]      = useState(() => new Date().toISOString().slice(0, 10));

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!clienteId) throw new Error('Cliente obrigatório');
      if (!sAssunto.trim()) throw new Error('Assunto obrigatório');
      const request: CriarAtendimentoRequest = {
        clienteId,
        sAssunto,
        sDescricao: sDescricao.trim() || undefined,
        prioridadeId: Number(prioridadeId),
        isResultadoEsperado,
        dtSolicitacao: new Date(dtSolicitacao).toISOString(),
      };
      return crmService.create(request);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['crm'] });
      toast.success('Atendimento criado com sucesso');
      navigate(`/operacional/crm/${data.id}`);
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/operacional/crm')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold">Novo Atendimento</h1>
      </div>

      <Card>
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Cliente <span className="text-destructive">*</span></Label>
            <PessoaSearchCombobox
              value={clienteId}
              onChange={setClienteId}
              placeholder="Buscar cliente..."
              filter="cliente"
            />
          </div>
          <div className="space-y-2">
            <Label>Data da Solicitação</Label>
            <Input type="date" value={dtSolicitacao}
              onChange={(e) => setDtSolicitacao(e.target.value)} />
          </div>
          <div className="md:col-span-2 space-y-2">
            <Label>Assunto <span className="text-destructive">*</span></Label>
            <Input value={sAssunto} onChange={(e) => setSAssunto(e.target.value)}
              placeholder="Descreva o assunto do atendimento" />
          </div>
          <div className="md:col-span-2 space-y-2">
            <Label>Descrição</Label>
            <Textarea value={sDescricao} onChange={(e) => setSDescricao(e.target.value)}
              maxLength={500} rows={4}
              placeholder="Detalhes do atendimento (até 500 caracteres)" />
          </div>
          <div className="space-y-2">
            <Label>Prioridade</Label>
            <Select value={prioridadeId} onValueChange={setPrioridadeId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Normal</SelectItem>
                <SelectItem value="2">Alta</SelectItem>
                <SelectItem value="3">Urgente</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Checkbox
              id="resultado"
              checked={isResultadoEsperado}
              onCheckedChange={(v) => setIsResultadoEsperado(!!v)}
            />
            <Label htmlFor="resultado" className="cursor-pointer">Resultado esperado obtido</Label>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/operacional/crm')}>Cancelar</Button>
        <Button className="gap-2" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
          <Save className="h-4 w-4" />Salvar
        </Button>
      </div>
    </div>
  );
}

// ── Dialog: adicionar contato ─────────────────────────────────────────────

interface AdicionarContatoDialogProps {
  atendimentoId: number;
  open: boolean;
  onClose: () => void;
}

function AdicionarContatoDialog({ atendimentoId, open, onClose }: AdicionarContatoDialogProps) {
  const queryClient = useQueryClient();
  const [sTexto,           setSTexto]          = useState('');
  const [dtContato,        setDtContato]        = useState(() => new Date().toISOString().slice(0, 10));
  const [dtRetorno,        setDtRetorno]        = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  });
  const [ibPrecisaRetornar, setIbPrecisaRetornar] = useState(false);

  const mutation = useMutation({
    mutationFn: () => {
      if (!sTexto.trim()) throw new Error('Texto do contato obrigatório');
      const request: AdicionarContatoRequest = {
        sTexto,
        dtContato: new Date(dtContato).toISOString(),
        dtRetorno: new Date(dtRetorno).toISOString(),
        ibPrecisaRetornar,
      };
      return crmService.adicionarContato(atendimentoId, request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-detalhe', String(atendimentoId)] });
      toast.success('Contato registrado');
      setSTexto(''); setIbPrecisaRetornar(false);
      onClose();
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Registrar Novo Contato</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Descrição do Contato <span className="text-destructive">*</span></Label>
            <Textarea value={sTexto} onChange={(e) => setSTexto(e.target.value)}
              placeholder="O que foi tratado neste contato..." rows={4} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Data do Contato</Label>
              <Input type="date" value={dtContato} onChange={(e) => setDtContato(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Data de Retorno</Label>
              <Input type="date" value={dtRetorno} onChange={(e) => setDtRetorno(e.target.value)} />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Checkbox id="retornar" checked={ibPrecisaRetornar}
              onCheckedChange={(v) => setIbPrecisaRetornar(!!v)} />
            <Label htmlFor="retornar" className="cursor-pointer">Precisa de retorno</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending} className="gap-2">
            <Save className="h-4 w-4" />Registrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Modo: detalhe ─────────────────────────────────────────────────────────

function AtendimentoDetalheView() {
  const navigate    = useNavigate();
  const { id }      = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [dialogOpen,   setDialogOpen]   = useState(false);
  const [whatsappOpen, setWhatsappOpen] = useState(false);

  const { data: atendimento, isLoading } = useQuery({
    queryKey: ['crm-detalhe', id],
    queryFn: () => crmService.getById(Number(id)),
    enabled: !!id,
  });

  const removerContatoMutation = useMutation({
    mutationFn: (contatoId: number) => crmService.removerContato(Number(id), contatoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-detalhe', id] });
      toast.success('Contato removido');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const concluirMutation = useMutation({
    mutationFn: () => crmService.update(Number(id), { isResultadoEsperado: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-detalhe', id] });
      queryClient.invalidateQueries({ queryKey: ['crm'] });
      toast.success('Atendimento concluído');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-center py-12 text-muted-foreground">Carregando...</div>
      </div>
    );
  }

  if (!atendimento) return null;

  return (
    <div className="p-6 space-y-4">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/operacional/crm')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Atendimento #{atendimento.id}</h1>
            <p className="text-muted-foreground text-sm mt-0.5">{atendimento.sAssunto}</p>
          </div>
          <Badge className={atendimento.isResultadoEsperado
            ? 'bg-green-500/10 text-green-500'
            : 'bg-blue-500/10 text-blue-500'}>
            {atendimento.isResultadoEsperado ? 'Concluído' : 'Pendente'}
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="gap-2 border-green-600 text-green-700 hover:bg-green-50 dark:hover:bg-green-950/30"
            onClick={() => setWhatsappOpen(true)}
          >
            <Send className="h-4 w-4" />
            WhatsApp
          </Button>
          {!atendimento.isResultadoEsperado && (
            <>
              <Button variant="outline" className="gap-2"
                onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4" />Registrar Contato
              </Button>
              <Button className="gap-2 bg-green-600 hover:bg-green-700"
                onClick={() => concluirMutation.mutate()}
                disabled={concluirMutation.isPending}>
                <CheckCircle2 className="h-4 w-4" />Concluir
              </Button>
            </>
          )}
        </div>
      </div>

      <EnviarWhatsAppModal
        open={whatsappOpen}
        onOpenChange={setWhatsappOpen}
        pessoaId={atendimento.clienteId ?? undefined}
        variaveisIniciais={{
          atendimento: String(atendimento.id),
          assunto:     atendimento.sAssunto ?? '',
        }}
      />

      {/* Info do atendimento */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Cliente ID</p>
              <p className="font-medium">{atendimento.clienteId}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Solicitação</p>
              <p className="font-medium">{new Date(atendimento.dtSolicitacao).toLocaleDateString('pt-BR')}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Prioridade</p>
              <p className="font-medium">
                {atendimento.prioridadeId === 3 ? 'Urgente' : atendimento.prioridadeId === 2 ? 'Alta' : 'Normal'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Total de Contatos</p>
              <p className="font-medium">{atendimento.interacoes?.length ?? 0}</p>
            </div>
          </div>
          {atendimento.sDescricao && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-muted-foreground text-sm">Descrição</p>
              <p className="font-medium whitespace-pre-wrap text-sm mt-1">{atendimento.sDescricao}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Timeline de contatos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Histórico de Contatos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!atendimento.interacoes || atendimento.interacoes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MessageSquare className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>Nenhum contato registrado ainda.</p>
              {!atendimento.isResultadoEsperado && (
                <Button variant="outline" className="mt-3 gap-2" onClick={() => setDialogOpen(true)}>
                  <Plus className="h-4 w-4" />Registrar primeiro contato
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {[...atendimento.interacoes]
                .sort((a, b) => new Date(b.dtContato).getTime() - new Date(a.dtContato).getTime())
                .map((c, idx) => (
                  <div key={c.id} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <MessageSquare className="h-4 w-4 text-primary" />
                      </div>
                      {idx < atendimento.interacoes.length - 1 && (
                        <div className="w-0.5 flex-1 bg-border mt-2" />
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CalendarClock className="h-3 w-3" />
                          {new Date(c.dtContato).toLocaleDateString('pt-BR')}
                          {c.ibPrecisaRetornar && (
                            <Badge className="bg-orange-500/10 text-orange-500 text-xs ml-2">
                              <Clock className="h-3 w-3 mr-1" />
                              Retorno: {new Date(c.dtRetorno).toLocaleDateString('pt-BR')}
                            </Badge>
                          )}
                        </div>
                        {!atendimento.isResultadoEsperado && (
                          <Button size="sm" variant="ghost"
                            onClick={() => removerContatoMutation.mutate(c.id)}>
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        )}
                      </div>
                      <div className="mt-2 p-3 bg-muted/40 rounded-lg text-sm whitespace-pre-wrap">
                        {c.sTexto}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AdicionarContatoDialog
        atendimentoId={atendimento.id}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </div>
  );
}

// ── Exportação principal ──────────────────────────────────────────────────

const CrmDetalhe = () => {
  const { id } = useParams<{ id: string }>();
  return id && id !== 'novo' ? <AtendimentoDetalheView /> : <NovoAtendimentoForm />;
};

export default CrmDetalhe;
